// PRE RMA — Calcul des modules (PEV, intrants, finance, décès, communication, pré-inventaire, inventaire).
// FONCTION PURE : mêmes entrées → mêmes sorties, sur le serveur (exports, API) ET dans le navigateur (écrans hors connexion).
// C'est l'unique endroit où les liens de données sont appliqués :
//   intrants RMA ⇄ pré-inventaire ⇄ inventaire ⇄ finance (voir inventaire.ts).
import { calculerFinance, controlerFinance, cleFinance, SECTIONS_FINANCE, CHAMPS_FIN_MOIS } from './finance.ts';
import type { SectionFinance, SaisieFinance } from './finance.ts';
import { stockFin, debutEffectif, controlerLigneStock, ligneStockVide } from './stock.ts';
import type { LigneStock } from './stock.ts';
import { PEV_INTRANTS, calculerIntrantPev, finPourReport } from './pevIntrants.ts';
import type { IntrantPev } from './pevIntrants.ts';
import { mensuelEffectif, totauxParAntigene, ventilationStrategie, PEV_ANTIGENES, ciblesMensuelles, courbeCumulee, tauxAbandon, anomaliesPev, tauxCouverture, tcvDtc1Mensuel, categoriserPeriodePev } from './pev.ts';
import type { PevSeance, PevMensuel } from './pev.ts';
import { agregerDeces, anomaliesDeces } from './causesDeces.ts';
import type { EvenementDeces } from './causesDeces.ts';
import { totauxCommunication } from './communication.ts';
import type { SeanceCommunication } from './communication.ts';
import {
  comptePre, sduEffectif, calculerLigneInventaire, totauxInventaire, controlerLigneInventaire, etatPreInventaire,
  ligneStockDepuisInventaire, controlerLienFinanceInventaire, indexMeManuels, cleTraceur, estCleTraceur,
} from './inventaire.ts';
import type { MedicamentCatalogue, PreLigne, LigneInventaire } from './inventaire.ts';

export interface ReportsModules {
  /** Stock début du mois = stock de fin (SDU effectif : saisi au RMA, sinon compté au pré-inventaire) du mois précédent. */
  stockSdu: Record<string, number>;
  /** Solde début = valeur physique / total de fin du mois précédent, par clé « SECTION|Ligne ». */
  financePhysique: Record<string, number>;
  financeCaisseBanque: Record<string, { ca: number; bq: number }>;
  /** Inventaire : stock fin (détail + gros) du mois précédent par médicament → stock début de ce mois. */
  inventaireFin: Record<string, number>;
  inventaireDemarre: boolean;
  /** Intrants PEV : stock fin du mois précédent par produit → stock début de ce mois (absent des anciens instantanés hors-ligne). */
  pevIntrantsFin?: Record<string, number>;
}
export const reportsVides = (): ReportsModules => ({ stockSdu: {}, financePhysique: {}, financeCaisseBanque: {}, inventaireFin: {}, inventaireDemarre: false, pevIntrantsFin: {} });

export interface SaisieModule { tableau: string; cle: string; donnees: any }

/**
 * Reports du mois N+1 à partir des saisies du mois N (fonction pure : serveur ET écran hors-ligne).
 *  - intrants : stock début = SDU effectif (saisi au RMA, sinon compté au pré-inventaire, sinon restant fin calculé) ;
 *  - inventaire : stock début = stock fin (détail + gros) ; finance : solde début = valeur/solde physique de fin.
 */
export function reportsDepuisSaisies(prec: SaisieModule[], reportsPrec?: Pick<ReportsModules, 'stockSdu'> | null): ReportsModules {
  const reports = reportsVides();
  const stockLignes: Record<string, Partial<LigneStock>> = {};
  const sduSaisi: Record<string, number> = {}, comptePreMap: Record<string, number> = {};
  const seancesPrec = prec.filter((x) => x.tableau === 'PEV_SEANCE').map((x) => ({ cle: x.cle, donnees: x.donnees }));
  for (const s of prec) {
    if (s.tableau === 'PEV_INTRANT') {
      const f = finPourReport(s.donnees as Partial<IntrantPev>, seancesPrec);
      if (f !== null) (reports.pevIntrantsFin ??= {})[s.cle] = f;
      continue;
    }
    if (s.tableau === 'STOCK') {
      if (!estCleTraceur(s.cle)) stockLignes[s.cle] = s.donnees ?? {};
      const sdu = s.donnees?.sdu;
      if (typeof sdu === 'number') sduSaisi[s.cle] = sdu;
    } else if (s.tableau === 'PRE_LIGNE') {
      const c = comptePre(s.donnees);
      if (c !== null) comptePreMap[s.cle] = c;
    } else if (s.tableau === 'INV_LIGNE') {
      reports.inventaireDemarre = true;
      reports.inventaireFin[s.cle] = calculerLigneInventaire(s.donnees, 0, null).fin;
    } else if (s.tableau === 'FINANCE') {
      const [section] = s.cle.split('|') as [SectionFinance];
      if (!SECTIONS_FINANCE.includes(section)) continue;
      // Comme dans le fichier de saisie : on ne reporte que si la fin de mois a été renseignée.
      const finRenseignee = CHAMPS_FIN_MOIS[section].some((k) => typeof s.donnees?.[k] === 'number' && s.donnees[k] !== 0);
      if (!finRenseignee) continue;
      reports.financePhysique[s.cle] = calculerFinance(section, s.donnees).physique;
      if (section !== 'MEDICAMENTS') reports.financeCaisseBanque[s.cle] = { ca: s.donnees.ca ?? 0, bq: s.donnees.bq ?? 0 };
    }
  }
  for (const cle of new Set([...Object.keys(sduSaisi), ...Object.keys(comptePreMap)])) {
    const v = sduEffectif(sduSaisi[cle], comptePreMap[cle]);
    if (v !== null) reports.stockSdu[cle] = v;
  }
  // Stock de fin du mois N = stock début du mois N+1, TOUJOURS : sans SDU saisi ni compté au pré-inventaire, on reporte le « restant fin » calculé
  // (début + reçue − sorties, jamais négatif). Le début du mois N est celui saisi, sinon celui reporté du mois N-1 (chaîne mois après mois).
  for (const [cle, brut] of Object.entries(stockLignes)) {
    if (cle in reports.stockSdu) continue;
    const l: LigneStock = { ...ligneStockVide(), ...brut };
    const d = debutEffectif(l, reportsPrec?.stockSdu?.[cle]);
    reports.stockSdu[cle] = Math.max(0, stockFin(l, d.valeur));
  }
  // Produit absent du mois N mais en stock à la fin du mois N-1 : le stock ne disparaît pas, il se poursuit.
  for (const [cle, v] of Object.entries(reportsPrec?.stockSdu ?? {})) {
    if (!(cle in reports.stockSdu) && v > 0 && !(cle in stockLignes)) reports.stockSdu[cle] = v;
  }
  return reports;
}
export interface EntreeModules {
  periode: { annee: number; mois: number };
  saisies: SaisieModule[];
  reports: ReportsModules;
  parametres: { popTotale: number | null };
  catalogue: MedicamentCatalogue[];
  /** PEV_MENSUEL de l'année (12 entrées, null = non saisi) — la courbe cumulée est annuelle. */
  pevAnnee: Array<PevMensuel | null>;
}

export function calculerModules(e: EntreeModules) {
  const { periode: p, saisies, reports, parametres: params, catalogue, pevAnnee } = e;
  const de = (t: string) => saisies.filter((s) => s.tableau === t);

  // --- PEV (T14)
  const seances: PevSeance[] = de('PEV_SEANCE').map((s) => s.donnees);
  const seancesCle = de('PEV_SEANCE').map((s) => ({ cle: s.cle, donnees: s.donnees as PevSeance }));
  const intrantsSaisis = new Map(de('PEV_INTRANT').map((s) => [s.cle, s.donnees as Partial<IntrantPev>]));
  const mensuelSaisi = (de('PEV_MENSUEL')[0]?.donnees ?? null) as PevMensuel | null;
  const mensuel = mensuelEffectif(mensuelSaisi, seances);
  const annee12 = [...pevAnnee]; annee12[p.mois - 1] = mensuel; // mois courant : toujours la valeur la plus à jour
  const pop = params.popTotale ?? 0;
  const cibles = ciblesMensuelles(pop);
  const totaux = totauxParAntigene(seances);
  const tcvDtc1Mois = mensuel ? tcvDtc1Mensuel(mensuel.dtc1, cibles.zeroOnze) : null;
  const abandonMois = mensuel ? tauxAbandon(mensuel.dtc1 ?? 0, mensuel.dtc3 ?? 0) : null;
  const courbe = courbeCumulee(annee12, pop);
  // PÉRIODE = de janvier au mois ouvert (ex. octobre → 10 mois) : vaccinés CUMULÉS ÷ (cible mensuelle × nombre de mois).
  // Le taux de couverture, le taux d'abandon et la catégorie portent sur cette période (null si population ou DTC1 absent : aucune catégorie inventée).
  const pt = courbe[p.mois - 1];
  const ciblePeriode = cibles.zeroOnze * p.mois; // cible mensuelle (0-11 mois) × nombre de mois, exactement
  const tcvDtc1Periode = pt ? tauxCouverture(pt.dtc1Cumul, ciblePeriode) : null;
  const abandonPeriode = pt ? pt.abandonCumul : null;
  const periodePev = { moisCumules: p.mois, ciblePeriode, dtc1Periode: pt?.dtc1Cumul ?? 0, dtc3Periode: pt?.dtc3Cumul ?? 0 };
  const pev = {
    popTotale: params.popTotale, cibles,
    totaux, ventilation: Object.fromEntries(PEV_ANTIGENES.map((a) => [a, ventilationStrategie(seances, a)])),
    couverture: Object.fromEntries(PEV_ANTIGENES.map((a) => [a, tauxCouverture(totaux[a], cibles.parAntigene[a])])),
    mensuel, mensuelSaisi, dtcAutomatique: { dtc1: mensuelSaisi?.dtc1 == null && seances.length > 0, dtc3: mensuelSaisi?.dtc3 == null && seances.length > 0 }, abandonMois, tcvDtc1Mois, tcvDtc1Periode, abandonPeriode, ...periodePev, categoriePeriode: categoriserPeriodePev(tcvDtc1Periode, abandonPeriode),
    anomalies: mensuel ? anomaliesPev(mensuel) : [],
    courbe,
    nbSeances: seances.length,
    // Intrants PEV : une ligne par produit (même vide), calculée avec le report du mois précédent
    intrants: PEV_INTRANTS.map((def) => {
      const saisie = intrantsSaisis.get(def.key) ?? null;
      return { cle: def.key, label: def.label, saisie, calcul: calculerIntrantPev(def, saisie, seancesCle, reports.pevIntrantsFin?.[def.key]) };
    }),
  };

  // --- Inventaire (FANOME) : stock début auto = fin du mois précédent ; recette = Σ dispensation × PU
  const invSaisi = new Map(de('INV_LIGNE').map((s) => [s.cle, s.donnees as Partial<LigneInventaire>]));
  const inventaire = catalogue.map((m) => {
    const l = invSaisi.get(m.id) ?? {};
    const c = calculerLigneInventaire(l, m.pu, reports.inventaireDemarre ? (reports.inventaireFin[m.id] ?? 0) : null);
    return { id: m.id, nom: m.nom, traceur: m.traceur, saisie: l, calcul: c, controle: controlerLigneInventaire(l, c), saisi: invSaisi.has(m.id) };
  });
  const depense = (de('INV_MOIS')[0]?.donnees?.depense ?? 0) as number;
  const totauxInv = totauxInventaire(inventaire.map((i) => i.calcul), depense);
  const calculInv = new Map(inventaire.map((i) => [i.id, i.calcul]));

  // --- Pré-inventaire (programmes) : compté (détail + gros) → SDU RMA ; sorties RMA déduites du stock
  const preSaisi = new Map(de('PRE_LIGNE').map((s) => [s.cle, s.donnees as Partial<PreLigne>]));

  // --- Intrants (T18) : lignes saisies + lignes pré-inventaire sans saisie RMA + médicaments traceurs (alimentés par l'inventaire)
  const lignesStock = new Map<string, Partial<LigneStock>>(de('STOCK').map((s) => [s.cle, s.donnees]));
  for (const cle of preSaisi.keys()) if (!lignesStock.has(cle)) lignesStock.set(cle, {});
  const stock: any[] = [];
  for (const [cle, brut] of lignesStock) {
    if (estCleTraceur(cle)) continue;
    const l: LigneStock = { ...ligneStockVide(), ...brut };
    const comptePreInv = comptePre(preSaisi.get(cle));
    const sduEff = sduEffectif(l.sdu, comptePreInv);
    const d = debutEffectif(l, reports.stockSdu[cle]);
    const restantFin = stockFin(l, d.valeur);
    // Stock de fin : SDU saisi, sinon compté au pré-inventaire, sinon RESTANT FIN calculé (début + reçue − sorties, jamais < 0).
    // C'est cette valeur, affichée et exportée, qui devient le stock début du mois suivant (même règle que reportsDepuisSaisies).
    const activite = d.valeur > 0 || [l.recue, l.utilisee, l.redeployee, l.perdue, l.sortieAc, l.debut].some((x) => x !== null && x !== undefined);
    const sduAutomatique = sduEff === null && activite;
    stock.push({
      cle, ...l, sdu: sduAutomatique ? Math.max(0, restantFin) : sduEff, sduAutomatique, sduSaisi: l.sdu, sduDepuisPreInventaire: l.sdu === null && comptePreInv !== null,
      debutEffectif: d.valeur, debutReporte: d.reporte, restantFin,
      controle: controlerLigneStock({ ...l, sdu: sduEff }, p.annee, p.mois, reports.stockSdu[cle]),
      preInventaire: etatPreInventaire(l, d.valeur, preSaisi.get(cle)),
    });
  }
  for (const m of catalogue.filter((x) => x.traceur)) {
    const cle = cleTraceur(m.id), manuel = (lignesStock.get(cle) ?? {}) as Partial<LigneStock>;
    const l = ligneStockDepuisInventaire(calculInv.get(m.id)!, manuel);
    stock.push({
      cle, nomMedicament: m.nom, alimenteParInventaire: true, ...l, sduSaisi: null, sduDepuisPreInventaire: false,
      debutEffectif: l.debut ?? 0, debutReporte: !!calculInv.get(m.id)?.reporte, restantFin: stockFin(l, l.debut ?? 0),
      controle: controlerLigneStock(l, p.annee, p.mois, null),
    });
  }
  const indexMe = indexMeManuels(catalogue);

  // --- Finance (T19) : + contrôle « sorties du mois = dispensation + périmés » de l'inventaire (section médicaments)
  const finance = de('FINANCE').map((s) => {
    const [section, ligne] = s.cle.split('|') as [SectionFinance, string];
    const rep = reports.financePhysique[cleFinance(section, ligne)] ?? null;
    const calcul = calculerFinance(section, s.donnees, rep);
    return {
      cle: s.cle, section, ligne, saisie: s.donnees as SaisieFinance, calcul, controle: controlerFinance(section, s.donnees, rep),
      lienInventaire: section === 'MEDICAMENTS' ? controlerLienFinanceInventaire(calcul.totalSorties, totauxInv) : null,
    };
  });

  // --- Décès (T20) et Communication (T3)
  const evts: EvenementDeces[] = de('CAUSES_DECES').map((s) => s.donnees);
  const grille = agregerDeces(evts);
  const seancesCom: SeanceCommunication[] = de('COMMUNICATION').map((s) => s.donnees);

  return {
    pev, stock, indexMeManuels: indexMe, finance,
    inventaire: { catalogue, lignes: inventaire, depense, totaux: totauxInv },
    causesDeces: { grille, anomalies: anomaliesDeces(grille), nbEvenements: evts.length },
    communication: { ...totauxCommunication(seancesCom), nbSeances: seancesCom.length },
    progression: {
      PEV: seances.length > 0 || !!mensuelSaisi || intrantsSaisis.size > 0, STOCK: stock.length > 0, FINANCE: finance.length > 0,
      CAUSES_DECES: evts.length > 0, COMMUNICATION: seancesCom.length > 0,
      INVENTAIRE: totauxInv.nbComptes > 0, PREINVENTAIRE: preSaisi.size > 0,
    },
  };
}
